// Import React Libraries
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams, withRouter } from "react-router-dom";

import { AppContext } from "../../providers/Redux/Reducers/AppContext";

// Import Ionic Components
import { IonButton, IonCol, IonGrid, IonLabel, IonList, IonLoading, IonRow, IonSpinner } from "@ionic/react";

// Import Css File
import "./product-detail.scss";
import styles from "./product-detail.module.scss";
import classNames from "classnames/bind";

// Import components
import MaxWidthContainer from "../../components/UI/MaxWidthContainer/MaxWidthContainer";
import ProductDetailInStock from "../../components/ProductDetail/ProductDetailInstock/ProductDetailInStock";
import ProductDetailRating from "../../components/ProductDetail/ProductDetailRating/ProductDetailRating";
import ProductDetailSlideBadges from "../../components/ProductDetail/ProductDetailSlideBadges/ProductDetailSlideBadges";
import ProductDetailCounter from "../../components/ProductDetail/ProductDetailCounter/ProductDetailCounter";
import ProductDetailInfo from "../../components/ProductDetail/ProductDetailInfo/ProductDetailInfo";
import { ProductType, Product as TProduct } from "@core/types";
import useGetProductByIdQuery from "../../providers/products/useGetProductByIdQuery";
import useGetCategoryByIdQuery from "../../providers/categories/useGetCategoryByIdQuery";
import useGetProductsQuery from "../../providers/products/useGetProductsQuery";
import { ProductDescription } from "../../components/UI/ProductDescription/ProductDescription";

import { AddToCartProduct, ProductId, useCartData } from "../../providers/cartDataStore";
import { ProductsSlider } from "../../components/ProductsSlider/ProductsSlider";
import useGetProductVariations from "../../providers/products/useGetProductVariations";
import { SelectVariationAttribute } from "../../fsd/features/SelectVatiationAttribute/SelectVatiationAttribute";
import Page from "../page-layout";
import { useConfig } from "../../providers/config";
import { AxiosError } from "axios";
import { useActiveOrMainShippingZone } from "../../providers/shippingZones";
import { Swiper, SwiperSlide } from "swiper/react";

import { Navigation } from "swiper/modules";
import { SmartImage } from "../../components/SmartImage/SmartImage";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/auth-context";
import { MainPageProduct } from "@core/components";

const cx = classNames.bind(styles);

export type ProductDetailParams = {
  id: string;
};

const ProductDetail = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const history = useHistory();
  const { search } = useLocation();
  const { state } = useContext(AppContext);
  // const swiperRef = useRef<SwiperRef | null>(null);
  const [showLoading, setShowLoading] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const ionContentRef = useRef<HTMLIonContentElement>(null);
  const { isUserAuthenticated } = useAuth();
  const { id } = useParams<ProductDetailParams>();

  const { activeShippingZone } = useActiveOrMainShippingZone();
  const { cartProductsMap, addToCart: addToCartStore, removeFromCart } = useCartData();

  const renderItem = (item: TProduct) => {
    return <MainPageProduct product={{ ...item, parentName: item.name }} lazyload={false} />;
  };

  useEffect(() => {
    ionContentRef.current!?.scrollToTop(500);
    if (!search.length) {
      selectedVariation = null;
      setSelectedVariation(null);
      // this costyl for temporary fix variables issue
    }
  }, [id]);

  const {
    data: product,
    isLoading: isSingleProductLoading,
    isFetching: isSingleProductFetching,
    error: productError,
  } = useGetProductByIdQuery(id, activeShippingZone.id, {
    options: {
      enabled: !!activeShippingZone.id,
    },
  });

  const { data: category, isLoading: isCategoryLoading } = useGetCategoryByIdQuery(product?.categories?.[0]?.id ?? "", {
    enabled: !!product?.categories?.length,
  });

  const { data: categoryProductsResults, isFetching: isCategoryProductsLoading } = useGetProductsQuery(
    {
      zoneId: activeShippingZone.id,
      categoryId: category?.id,
    },
    {
      enabled: !!category?.id && !!activeShippingZone.id,
    },
  );

  const categoryProducts = categoryProductsResults?.products;

  const { data: variations, isFetching: isVariationsFetching } = useGetProductVariations(
    product?.variations ?? [],
    activeShippingZone.id,
    {
      enabled: !!product?.variations?.length && !!activeShippingZone.id,
    },
  );

  let [selectedVariation, setSelectedVariation] = useState<TProduct | null>(null); // For Selected Variation

  const productOrSelectedVariation = product?.type === ProductType.VARIABLE ? selectedVariation : product;

  const productId = productOrSelectedVariation?.id;
  const stockQuantity = productOrSelectedVariation?.stockQuantity!;
  const isInStock = productOrSelectedVariation?.stockStatus === "instock";

  function addToCart(product: TProduct) {
    // DON'T ALLOW ADD TO CART IF PRODUCT UNPURCHASABLE
    if (!product?.purchasable) {
      return;
    }

    const productItem = product.type === ProductType.VARIABLE ? selectedVariation! : product;
    addToCartStore(productItem as AddToCartProduct);
  }

  function handleRemoveProductFromCart(id: ProductId) {
    removeFromCart(id, isUserAuthenticated);
  }

  // For New Product Check Badge
  function checkProductNew() {
    if (product && product.dateCreated) {
      const pDate = new Date(product.dateCreated);
      const date = pDate.getTime() + state.config.newProductDuration * 86400000;
      const todayDate = new Date().getTime();

      return date > todayDate;
    }
  }

  const openProducts = (value: string) => {
    history.push(`/${value}`);
  };

  const handleImageLoad = (isImageLoaded: boolean) => {
    if (isImageLoaded) {
      setImagesLoaded(true);
    }
  };

  const handleVariationSelect = (variation: TProduct | null) => {
    // if (variation?.images[0].id && swiperRef?.current) {
    // const variationImgIndex = product?.images?.findIndex((img) => img.id === variation.images[0].id);
    // variationImgIndex && variationImgIndex >= 0 && swiperRef.current.swiper.slideTo(variationImgIndex);
    // }
    setSelectedVariation(variation);
  };

  const headerBreadcrumbs =
    !isSingleProductLoading || !isCategoryLoading || !isCategoryProductsLoading || !isSingleProductFetching
      ? [
          {
            title: category ? category.name : t("Catalog"),
            handler: () => {
              return category
                ? openProducts(`products/${category.id}/${category.name}/popular`)
                : openProducts("search-main-categories");
            },
          },
          { title: product?.name ?? "" },
        ]
      : [];

  const goHome = () => {
    history.push("/home3");
  };

  const productNotFound =
    !isSingleProductLoading && !isSingleProductFetching && (productError as AxiosError)?.response?.status === 404;

  const hasMultipleImages = !isSingleProductLoading && !isSingleProductFetching && product?.images.length! > 1;

  const hasSwiperNavigation = hasMultipleImages && imagesLoaded;

  const swiperNavigation = hasSwiperNavigation
    ? {
        enabled: true,
      }
    : {
        enabled: false,
        prevEl: null,
        nextEl: null,
      };

  return (
    <Page>
      <Page.HeaderMobile showBackButton />

      <Page.Content ref={ionContentRef}>
        <Page.HeaderDesktop breadcrumbs={headerBreadcrumbs} />
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          message={t("Please wait...")}
          duration={20000}
        />
        <IonLoading
          isOpen={isSingleProductLoading || isSingleProductFetching}
          message={t("Please wait...")}
          duration={20000}
        />
        <div
          className={`${cx(
            "productDetailWrapper",
            "product-detail-page",
            "productDetailPage",
            productNotFound && "flex",
          )} global-content`}
        >
          <MaxWidthContainer
            smContainer={true}
            mdContainer={true}
            lgContainer={true}
            classList={cx("productDetailContainer", productNotFound && "flex-1")}
          >
            {!!product ? (
              <div>
                <IonRow className={cx("productDetailTop")}>
                  <IonCol sizeMd={"5"} className={cx("productDetailSliderWrapper")}>
                    <Swiper
                      spaceBetween={4}
                      slidesPerView={1}
                      modules={[Navigation]}
                      className={cx("productDetailSlider")}
                      navigation={swiperNavigation}
                    >
                      {!!product.videoUrl?.length && (
                        <SwiperSlide className={cx("productDetailSliderItem")}>
                          <video height={"100%"} className={cx("productDetailSlider__video")} controls>
                            <source src={product.videoUrl} type="video/mp4" />
                          </video>
                        </SwiperSlide>
                      )}
                      {product.images?.length === 0 ? (
                        <SwiperSlide className={cx("productDetailSliderItem")}>
                          <ProductDetailSlideBadges
                            product={product}
                            selectedVariation={selectedVariation}
                            isNewProduct={checkProductNew()!}
                          />
                          <div className={cx("slideImgWrapper")}>
                            <SmartImage image={undefined} className={cx("slideImg")} />
                          </div>
                        </SwiperSlide>
                      ) : (
                        product.images?.map((img) => (
                          <SwiperSlide key={img.id}>
                            <ProductDetailSlideBadges
                              product={product}
                              selectedVariation={selectedVariation}
                              isNewProduct={checkProductNew()!}
                            />
                            <div className={cx("slideImgWrapper")}>
                              <SmartImage image={img} isImageLoadedFn={handleImageLoad} className={cx("slideImg")} />
                            </div>
                          </SwiperSlide>
                        ))
                      )}
                    </Swiper>
                  </IonCol>
                  <IonCol sizeMd={"7"} className={cx("ion-hide-md-down")}>
                    <IonRow className={cx("productDetailTitleRow")}>
                      <IonCol className="productDetailTitleCol" size="12">
                        <h3 className={cx("productDetailTitle")}>{product?.name}</h3>
                      </IonCol>
                    </IonRow>
                    <IonRow>
                      <IonCol size="6">
                        <ProductDetailInStock
                          portionUnit={product.portionUnit ?? ""}
                          stockQuantity={stockQuantity}
                          label={!product?.purchasable ? t("Purchase is limited in time") : ""}
                          isInStock={isInStock}
                        />
                      </IonCol>
                      <IonCol size="6" className={cx("averageRatingWrapper")}>
                        {product.averageRating && (
                          <ProductDetailRating
                            rating={
                              product.averageRating ? product.averageRating?.slice(0, 3).replace(".", ",") : undefined
                            }
                          />
                        )}
                      </IonCol>
                    </IonRow>
                    {isVariationsFetching && (
                      <IonGrid>
                        <IonCol>
                          <IonSpinner />
                        </IonCol>
                      </IonGrid>
                    )}
                    {variations && product.type === ProductType.VARIABLE && (
                      <SelectVariationAttribute
                        variations={variations as TProduct[]}
                        onVariationSelect={handleVariationSelect}
                      />
                    )}
                    {(product.type !== ProductType.VARIABLE ||
                      (product.type === ProductType.VARIABLE && selectedVariation)) &&
                      productId && (
                        <ProductDetailCounter
                          isInStock={isInStock}
                          isEmpty={!cartProductsMap[productId]?.quantity}
                          product={product}
                          selectedVariation={selectedVariation}
                          cartQuantity={cartProductsMap[productId]?.quantity ?? 0}
                          onAddToCart={addToCart}
                          onRemoveFromCart={handleRemoveProductFromCart}
                        />
                      )}
                  </IonCol>
                </IonRow>

                <IonGrid className={cx("ion-hide-md-up", "productDetailHeadInfo")}>
                  <IonRow>
                    <IonCol size="6">
                      <ProductDetailInStock
                        stockQuantity={stockQuantity}
                        portionUnit={product.portionUnit ?? ""}
                        label={!product?.purchasable ? t("Purchase is limited in time") : ""}
                        isInStock={isInStock}
                      />
                    </IonCol>
                    <IonCol size="6" className={cx("averageRatingWrapper")}>
                      {product.averageRating && (
                        <ProductDetailRating
                          rating={
                            product.averageRating ? product.averageRating?.slice(0, 3).replace(".", ",") : undefined
                          }
                        />
                      )}
                    </IonCol>
                  </IonRow>
                  <IonRow>
                    <IonCol size="12">
                      <h3 className={cx("productDetailItemTitle")}>{product?.name}</h3>
                    </IonCol>
                  </IonRow>
                </IonGrid>
                {/*  -------------------------- For Variable Products ---------------------------- */}
                {isVariationsFetching && (
                  <IonGrid className={cx("ion-hide-md-up")}>
                    <IonCol>
                      <IonSpinner />
                    </IonCol>
                  </IonGrid>
                )}
                {product.type === ProductType.VARIABLE && !isVariationsFetching && (
                  <IonGrid className={cx("productDetailAttributesGrid", "ion-hide-md-up")}>
                    <IonList>
                      {variations && product.type === ProductType.VARIABLE && !isVariationsFetching && (
                        <SelectVariationAttribute
                          variations={variations as TProduct[]}
                          onVariationSelect={handleVariationSelect}
                        />
                      )}
                    </IonList>
                  </IonGrid>
                )}
                {config?.showProductReviews ? (
                  <div className={cx("productDetailInfo")}>
                    <ProductDetailInfo id={id} product={product} />
                  </div>
                ) : (
                  <IonGrid>
                    <IonRow>
                      <IonCol>
                        <h3 className={"ion-no-margin ion-margin-bottom"}>{t("Product Description")}</h3>
                        <ProductDescription props={product} />
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                )}
                <IonGrid class={cx("sameCategorySlider", "productDetailContent animate-product ion-no-padding")}>
                  {isCategoryProductsLoading && (
                    <IonCol>
                      <IonSpinner className="ion-margin-start" />
                    </IonCol>
                  )}
                  {categoryProducts && categoryProducts?.length > 0 && !isCategoryProductsLoading && (
                    <IonRow class="ion-no-padding">
                      <IonCol class="ion-no-padding" size="12">
                        <IonRow class="top-icon-header heading">
                          <p className={cx("relatedItems")}>{t("Products of this category")}</p>
                        </IonRow>
                        <div className={cx("sameCategorySliderWrapper")}>
                          <ProductsSlider items={categoryProducts} renderItem={renderItem} />
                        </div>
                      </IonCol>
                    </IonRow>
                  )}
                </IonGrid>
              </div>
            ) : productNotFound ? (
              <div className={cx("h-full flex justify-center items-center flex-col")}>
                <div>{t("No item found")}</div>
                <IonButton onClick={goHome}>{t("Go to home")}</IonButton>
              </div>
            ) : null}
          </MaxWidthContainer>
        </div>
        <Page.ContentFooter />
      </Page.Content>

      <Page.PageFooter>
        {product && productId && (
          <div className={cx("ion-hide-md-up")}>
            {(product.type !== ProductType.VARIABLE ||
              (product.type === ProductType.VARIABLE && selectedVariation)) && (
              <ProductDetailCounter
                isInStock={isInStock}
                isEmpty={!cartProductsMap[productId]?.quantity}
                product={product}
                selectedVariation={selectedVariation}
                cartQuantity={cartProductsMap[productId]?.quantity ?? 0}
                onAddToCart={addToCart}
                onRemoveFromCart={handleRemoveProductFromCart}
              />
            )}
          </div>
        )}
      </Page.PageFooter>
    </Page>
  );
};

export default withRouter(ProductDetail);
